import { AUDIENCE_SCOPE, NOTIFICATION_CATEGORY, NOTIFICATION_SEVERITY } from './Enums';

/**
 * Communications: every message the service is capable of sending, and the four things an admin
 * may change about one without a redeploy — whether it happens at all, whether it reaches the
 * browser, whether it is mailed, what it says, and who it goes to. Mirrors `CommunicationView`,
 * `Update`, `CommunicationChange` and `Preview` from the allocation service — see
 * README-communications.md there for the contract.
 *
 * Deliberately the same design as Parameter, down to the naming, and the same three things are
 * easy to get wrong:
 *
 * - It is not an entity. There is no id and no `BaseEntity`, because the list is the service's
 *   *catalog* of communications rather than a table: one nobody has overridden has no row at all.
 *   `name` is the identity, and it is what `/communications/{key}` is addressed by.
 * - **Every field is resolved.** `subjectTemplate` is what the service will actually use, which
 *   may have come from the override row or from the catalog constant. `overridden` is the only
 *   thing that says which, because several fields hold the same text at both layers.
 * - **A PUT is a full replacement, not a patch**, and a null field means *no override* rather than
 *   *empty*. So an editor has to send every field back, and send null for anything that should go
 *   on tracking the code. See `updateFrom`.
 */

/** One entry in a to/cc/bcc list, as `RecipientSpec` holds it. */
export interface RecipientToken {
    /** A `RecipientSource` constant. Kept as a string: the catalog is the service's. */
    source: string;
    /** The address, role or parameter name for a standalone source; null for an entity derived one. */
    value: string | null;
}

/** One `RecipientSource`, as `GET /communications/recipient-sources` describes it. */
export interface RecipientSourceInfo {
    name: string;
    /** Whether the token has to carry a value after the colon. */
    needsValue: boolean;
    /** Whether one token can resolve to several addresses, which is what belongs in BCC. */
    multiValued: boolean;
    /** The `Notification.targetEntity` this source resolves against, or null when it needs none. */
    targetEntity: string | null;
}

export interface Communication {
    /** The catalog constant, e.g. `KLASS_DEADLINE_REMINDER`. The stable identity, and what
     * `notification.type` stores, so renaming one on the service orphans its overrides. */
    name: string;
    category: NOTIFICATION_CATEGORY;
    severity: NOTIFICATION_SEVERITY;
    description: string;
    /**
     * Whether the message templates may be edited at all. False for a communication whose
     * producers supply the whole wording themselves — a `PUT` carrying a message template for one
     * of those is refused, because a box whose contents were never used is worse than no box.
     */
    templatable: boolean;
    /** What a template here may refer to, in the order they read in the default. Empty when not
     * templatable. */
    placeholders: string[];

    /** The gate. Off means nothing is written, pushed or emailed. */
    enabled: boolean;
    /** Whether it reaches the browser. Never removes the inbox row. */
    push: boolean;
    email: boolean;
    /**
     * The service-wide email kill switch, the NOTIFICATION_EMAIL_ENABLED setting. Not a property
     * of this communication — it is repeated on every one — and while it is off, `email` here is
     * an intention rather than a behaviour.
     */
    emailMasterSwitch: boolean;

    /** In force. Null only where the catalog declares none — a literal communication has no
     * message template of its own. Same for the five below. */
    subjectTemplate: string | null;
    pushMessageTemplate: string | null;
    emailBodyTemplate: string | null;
    /** In force, as a comma separated `RecipientSpec` list. Canonically formatted by the service,
     * which is why the defaults below have to be compared to it by parsing rather than as text. */
    to: string | null;
    cc: string | null;
    bcc: string | null;

    /** What the catalog constant declares, so the screen can say what a reset would give. */
    defaultSubjectTemplate: string | null;
    defaultPushMessageTemplate: string | null;
    defaultEmailBodyTemplate: string | null;
    defaultTo: string | null;
    defaultCc: string | null;
    defaultBcc: string | null;
    defaultEmail: boolean;

    /** Which fields the override row is answering for. The `COMMUNICATION_FIELD` values. */
    overridden: string[];

    /** Who can *see* the notification, which since recipients arrived is a different question
     * from who is mailed. Read only here: the audience is the producer's, not an operator's. */
    defaultAudienceScope: AUDIENCE_SCOPE;
    defaultAudienceValue: string | null;

    /** Legal but probably not meant — emailing nobody, a multi-valued source on a visible line, a
     * class recipient on a communication raised about something else. Warnings rather than
     * rejections because each has a reading under which it is exactly what was wanted. */
    warnings: string[];
}

/** The overridable fields, spelled as the service reports them in `overridden`. */
export enum COMMUNICATION_FIELD {
    ENABLED = 'enabled',
    PUSH = 'push',
    EMAIL = 'email',
    SUBJECT_TEMPLATE = 'subjectTemplate',
    PUSH_MESSAGE_TEMPLATE = 'pushMessageTemplate',
    EMAIL_BODY_TEMPLATE = 'emailBodyTemplate',
    TO = 'to',
    CC = 'cc',
    BCC = 'bcc',
}

/**
 * The body of a `PUT`. Every field nullable, and **null means no override** rather than empty, so
 * this is a full replacement: sending `{"email": false}` turns email off *and puts everything else
 * back to the catalog default.
 */
export interface CommunicationUpdate {
    enabled: boolean | null;
    push: boolean | null;
    email: boolean | null;
    subjectTemplate: string | null;
    pushMessageTemplate: string | null;
    emailBodyTemplate: string | null;
    to: string | null;
    cc: string | null;
    bcc: string | null;
}

/** One audited change to one communication, newest first as the endpoint returns them. */
export interface CommunicationChange {
    revision: number;
    changedAt: string;
    /** A principal, `system` for a change with no user behind it, or null when the change
     * predates attribution. */
    username?: string | null;
    type: 'ADD' | 'MOD' | 'DEL';
    /** The override row as it stood after the change. Every field null on a DEL, and null on the
     * others means that column held no override at that revision. */
    enabled?: boolean | null;
    push?: boolean | null;
    email?: boolean | null;
    subjectTemplate?: string | null;
    pushMessageTemplate?: string | null;
    emailBodyTemplate?: string | null;
    to?: string | null;
    cc?: string | null;
    bcc?: string | null;
}

/** What a communication renders to, without sending it. Null where there is no template. */
export interface CommunicationPreview {
    subject: string | null;
    message: string | null;
    emailBody: string | null;
    /** Renders that failed. The preview reports them instead of falling back, unlike the send
     * path, because here somebody is waiting to be told. */
    problems: string[];
}

/**
 * What `enabled` and `push` fall back to.
 *
 * Hardcoded here because the service hardcodes them too — `CommunicationService.merge` picks
 * against `Boolean.TRUE` for both, so unlike `email` there is no `defaultEnabled` on the view to
 * read. A communication is on and pushed unless somebody said otherwise.
 */
export const DEFAULT_ENABLED = true;
export const DEFAULT_PUSH = true;

// ------------------------------------------------------------------------------------------
// Recipients
// ------------------------------------------------------------------------------------------

/** What separates one token from the next, matching `RecipientSpec.SEPARATOR`. */
const TOKEN_SEPARATOR = ',';

/**
 * Splits a recipient list the way `RecipientSpec.parseList` splits it, blanks dropped.
 *
 * Lenient on purpose, and this is the counterpart of the lenient parse the service does when it
 * resolves: a token naming a source this build has never heard of comes back as a token with that
 * source name, so the editor can show it and the operator can remove it. The strict check is
 * `validateRecipients`, which runs against the sources the service says it accepts.
 */
export const parseRecipientList = (text: string | null | undefined): RecipientToken[] =>
    (text ?? '')
        .split(TOKEN_SEPARATOR)
        .map(token => token.trim())
        .filter(token => token.length > 0)
        .map(token => {
            const split = token.indexOf(':');
            const source = (split < 0 ? token : token.slice(0, split)).trim().toUpperCase();
            const value = split < 0 ? null : token.slice(split + 1).trim();
            return { source, value: value ? value : null };
        });

/** The token form, which is what the column holds. Round trips through `parseRecipientList`. */
export const formatRecipientList = (tokens: RecipientToken[]): string | null =>
    tokens.length === 0
        ? null
        : tokens.map(token => (token.value ? `${token.source}:${token.value}` : token.source)).join(TOKEN_SEPARATOR);

export const formatRecipientToken = (token: RecipientToken): string =>
    token.value ? `${token.source}:${token.value}` : token.source;

/**
 * Two recipient lists that mean the same thing.
 *
 * Text comparison will not do: the service returns the value in force canonically formatted, from
 * parsed specs, while the default it reports beside it is the raw string the catalog constant
 * declares. `"ROLE:admin, KLASS_TSS"` and `"ROLE:admin,KLASS_TSS"` are the same list, and treating
 * them as different would have the editor store an override that changes nothing.
 */
export const sameRecipients = (left: string | null, right: string | null): boolean => {
    const a = parseRecipientList(left);
    const b = parseRecipientList(right);
    return a.length === b.length && a.every((token, index) => {
        const other = b[index];
        return !!other && token.source === other.source && (token.value ?? '') === (other.value ?? '');
    });
};

/**
 * The same rules `RecipientSpec.parse` applies on the server, so a mistyped token is caught before
 * it costs a round trip — which matters more here than it does for a parameter, because a 400 from
 * this endpoint comes back with an **empty body** and reads as "an unknown error occurred".
 *
 * Advisory only: the service validates every write regardless and remains the authority.
 *
 * @param sources what `GET /communications/recipient-sources` returned. Empty means it could not
 *     be read, and nothing is validated rather than everything being called wrong.
 * @returns why the list is not legal, or null when it is.
 */
export const validateRecipients = (
    text: string | null,
    sources: RecipientSourceInfo[]
): string | null => {
    if (sources.length === 0) return null;

    for (const token of parseRecipientList(text)) {
        const source = sources.find(candidate => candidate.name === token.source);
        if (!source) {
            return `${token.source} is not a recipient source.`;
        }
        if (source.needsValue && !token.value) {
            return `${source.name} needs a value, as in ${source.name}:something.`;
        }
        if (!source.needsValue && token.value) {
            // Refused rather than ignored, as the service refuses it: a value here means whoever
            // wrote it believed it did something.
            return `${source.name} takes no value — it resolves against the notification's `
                + `${source.targetEntity} target.`;
        }
    }
    return null;
};

// ------------------------------------------------------------------------------------------
// Templates
// ------------------------------------------------------------------------------------------

/**
 * Words a Qute expression can contain that are not names of values. `it` and `this` resolve
 * against the enclosing section rather than the data map, so neither is a placeholder.
 */
const QUTE_KEYWORDS = new Set([
    'true', 'false', 'null', 'and', 'or', 'not', 'is', 'in', 'else', 'it', 'this'
]);

/** `{! a comment !}`, which contains nothing to resolve. */
const COMMENT = /\{![\s\S]*?!\}/g;

/** One `{...}` group. Qute expressions in a notification template do not nest braces. */
const EXPRESSION = /\{([^{}]*)\}/g;

/** A quoted literal, which routinely contains words that read like placeholders —
 * `{courseName ?: 'unknown course'}` is in the catalog today. */
const QUOTED = /'[^']*'|"[^"]*"/g;

/** An identifier, optionally namespaced as `config:foo`, which is how the service spells one. */
const IDENTIFIER = /[A-Za-z_][A-Za-z0-9_]*(?::[A-Za-z_][A-Za-z0-9_]*)?/g;

/** The leading name of every value one expression refers to. */
const namesIn = (expression: string): string[] => {
    const searchable = expression.replace(QUOTED, ' ');
    const names: string[] = [];

    IDENTIFIER.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = IDENTIFIER.exec(searchable)) !== null) {
        // Only the first part of a path. `{klassRef}` and `{klassRef.length}` both refer to
        // klassRef, and whether a further part resolves is a question about the value.
        if (searchable[match.index - 1] === '.') continue;
        if (QUTE_KEYWORDS.has(match[0])) continue;
        names.push(match[0]);
    }
    return names;
};

/**
 * The placeholders a template refers to that the communication does not declare.
 *
 * This is the check that makes the template box safe to offer. Quarkus runs Qute with strict
 * rendering, so a template naming `{klassName}` when the producer passes `{courseName}` does not
 * degrade to a blank — it throws when the notification is raised, and a communication that cannot
 * render is one that is never delivered.
 *
 * A best-effort reading of the same rule `TemplateRenderer.validate` enforces, not a Qute parser:
 * it walks `{...}` groups, takes the parameters of a `{#section}` and skips its tag name, and
 * ignores comments, closing tags and quoted literals. Deliberately conservative about what it
 * calls a name, because refusing a template the service would have accepted leaves an operator
 * with no way forward, while missing one costs a round trip.
 */
export const unknownPlaceholders = (template: string | null, allowed: string[]): string[] => {
    if (!template || template.trim().length === 0) return [];

    const declared = new Set(allowed);
    const unknown: string[] = [];
    const body = template.replace(COMMENT, ' ');

    EXPRESSION.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = EXPRESSION.exec(body)) !== null) {
        let expression = (match[1] ?? '').trim();
        if (expression.length === 0) continue;
        // A closing tag, `{/if}`, and an unparsed-section marker name nothing.
        if (expression.startsWith('/') || expression.startsWith('@')) continue;
        if (expression.startsWith('#')) {
            // A section: `{#if klassRef}`. The tag name is Qute's, its parameters are not.
            expression = expression.slice(1).replace(/^[A-Za-z_][A-Za-z0-9_]*/, '');
        }

        for (const name of namesIn(expression)) {
            if (!declared.has(name) && !unknown.includes(name)) {
                unknown.push(name);
            }
        }
    }
    return unknown;
};

/**
 * Why this template cannot be saved, or null when it can.
 *
 * @param allowed the communication's declared placeholders. Empty for a literal communication,
 *     whose subject may still be edited but may refer to nothing.
 */
export const validateTemplate = (template: string | null, allowed: string[]): string | null => {
    const unknown = unknownPlaceholders(template, allowed);
    if (unknown.length === 0) return null;

    return allowed.length === 0
        ? `This communication declares no placeholders, so ${unknown.join(', ')} would never have a `
            + 'value and the notification would fail to render.'
        : `${unknown.join(', ')} ${unknown.length === 1 ? 'is not a placeholder' : 'are not placeholders'} `
            + `this communication passes. It passes ${allowed.join(', ')}.`;
};

// ------------------------------------------------------------------------------------------
// Overrides
// ------------------------------------------------------------------------------------------

/** True when the table is answering for at least one field, which is the only case a reset has
 * anything to drop. */
export const isOverridden = (communication: Communication): boolean =>
    communication.overridden.length > 0;

export const isFieldOverridden = (communication: Communication, field: COMMUNICATION_FIELD): boolean =>
    communication.overridden.includes(field);

/** Blank counts as null throughout, so "cleared in the editor" and "never set" are one state —
 * they have to be, because an empty subject template renders to a mail with no subject rather
 * than to the default the operator expected to fall back to. */
const blankToNull = (value: string | null | undefined): string | null =>
    value == null || value.trim().length === 0 ? null : value;

/**
 * The editable state of one communication: what is in force, whatever layer answered for it.
 *
 * Held as the resolved values rather than as "the override, if any" because that is what an
 * operator is editing — the wording that goes out. Which layer it came from is `overridden`, and
 * it is restated on the way back out by `updateFrom`.
 */
export interface CommunicationDraft {
    enabled: boolean;
    push: boolean;
    email: boolean;
    subjectTemplate: string;
    pushMessageTemplate: string;
    emailBodyTemplate: string;
    to: string | null;
    cc: string | null;
    bcc: string | null;
}

export const draftFrom = (communication: Communication): CommunicationDraft => ({
    enabled: communication.enabled,
    push: communication.push,
    email: communication.email,
    subjectTemplate: communication.subjectTemplate ?? '',
    pushMessageTemplate: communication.pushMessageTemplate ?? '',
    emailBodyTemplate: communication.emailBodyTemplate ?? '',
    to: communication.to,
    cc: communication.cc,
    bcc: communication.bcc,
});

/**
 * The `PUT` body for a draft: every field that differs from what the catalog declares, and null
 * for every field that does not.
 *
 * **Null is the whole design, and it is why this cannot simply send what is on screen.** Null does
 * not mean "off" or "empty", it means *this communication has no opinion here, use the catalog's*.
 * So a value identical to the default is sent as null rather than stored: an operator who turns
 * email off for one communication writes one column, and its wording and recipients keep tracking
 * the code as it changes. A row of non-null columns mirroring the constant would freeze all of it
 * the moment somebody first touched anything.
 */
export const updateFrom = (
    communication: Communication,
    draft: CommunicationDraft
): CommunicationUpdate => {
    const template = (value: string, declared: string | null): string | null => {
        const next = blankToNull(value);
        return next === blankToNull(declared) ? null : next;
    };
    const recipients = (value: string | null, declared: string | null): string | null =>
        sameRecipients(value, declared) ? null : blankToNull(value);

    return {
        enabled: draft.enabled === DEFAULT_ENABLED ? null : draft.enabled,
        push: draft.push === DEFAULT_PUSH ? null : draft.push,
        email: draft.email === communication.defaultEmail ? null : draft.email,
        subjectTemplate: template(draft.subjectTemplate, communication.defaultSubjectTemplate),
        // A literal communication refuses a message template outright, so the fields the editor
        // does not offer are never sent — not even carrying the default straight back.
        pushMessageTemplate: communication.templatable
            ? template(draft.pushMessageTemplate, communication.defaultPushMessageTemplate)
            : null,
        emailBodyTemplate: communication.templatable
            ? template(draft.emailBodyTemplate, communication.defaultEmailBodyTemplate)
            : null,
        to: recipients(draft.to, communication.defaultTo),
        cc: recipients(draft.cc, communication.defaultCc),
        bcc: recipients(draft.bcc, communication.defaultBcc),
    };
};

/** Whether a draft would write anything the communication is not already saying. Keeps a no-op
 * save off the audit trail, which is what an operator reads to find out what actually changed. */
export const hasChanges = (communication: Communication, draft: CommunicationDraft): boolean => {
    const update = updateFrom(communication, draft);
    const current = updateFrom(communication, draftFrom(communication));
    return (Object.keys(update) as (keyof CommunicationUpdate)[])
        .some(field => update[field] !== current[field]);
};
